import React, { ReactNode } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { motion } from 'framer-motion';
import cx from 'classnames';

export interface IHeroCardProps {
  imageUrl?: any;
  heroImage?: any;
  imageAltText?: string;
  isLiveEvent?: boolean;
  videoPlayer?: ReactNode;
  isPlayingTrailer?: boolean;
  isFullScreen?: boolean;
  hasGradient: boolean;
  isCustomLandingPage?: boolean;
}

const HeroCard = ({
  hasGradient,
  imageUrl,
  heroImage,
  imageAltText = '',
  isLiveEvent = false,
  videoPlayer,
  isPlayingTrailer = false,
  isFullScreen = false,
  isCustomLandingPage = false,
}: IHeroCardProps) => {
  const baseImageUrl = `${imageUrl}?twccategory=Hero`;

  let heroImageElement: ReactNode;

  if (heroImage) {
    if (heroImage.childImageSharp) {
      heroImageElement = (
        <GatsbyImage
          image={heroImage?.childImageSharp?.gatsbyImageData}
          alt={imageAltText}
          className="hero-banner__image"
        />
      );
    } else {
      heroImageElement = (
        <img
          src={heroImage}
          alt={imageAltText}
          className="hero-banner__image"
        />
      );
    }
  } else {
    heroImageElement = (
      <motion.picture
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.1, ease: 'linear' }}
      >
        <source
          srcSet={`${baseImageUrl}&width=1800`}
          media="(min-width: 1600px)"
        />
        <source
          srcSet={`${baseImageUrl}&width=1401`}
          media="(min-width: 1201px) and (max-width: 1600px)"
        />
        <source
          srcSet={`${baseImageUrl}&width=1200 `}
          media="(min-width: 800px) and (max-width: 1200px)"
        />
        <source
          srcSet={`${baseImageUrl}&width=750`}
          media="(min-width: 501px) and (max-width: 800px)"
        />
        <source
          srcSet={`${baseImageUrl}&width=450`}
          media="(min-width: 0px) and (max-width: 500px)"
        />
        <img
          width="800"
          src={`${baseImageUrl}&width=800`}
          alt={imageAltText}
          className="hero-banner__image"
        />
      </motion.picture>
    );
  }

  return (
    <motion.div
      className={cx({
        'hero-banner__media-wrapper': true,
        'hero-banner__media-wrapper--nogradient': !hasGradient,
        'hero-banner__media-wrapper--landingpage': isCustomLandingPage,
      })}
      animate={isPlayingTrailer || isFullScreen ? 'playing' : 'static'}
    >
      {/* LIVE Banner */}
      {isLiveEvent && <span className="hero-banner__live-banner">LIVE</span>}

      <motion.div
        className={cx({
          'hero-banner__image-crop': true,
          'hero-banner__image-crop-nogradient': !hasGradient,
          'hero-banner__image-crop-landingpage': isCustomLandingPage,
        })}
        variants={{
          playing: { opacity: 0 },
          static: { opacity: 1 },
        }}
        transition={{ duration: 0.3 }}
      >
        {heroImageElement}
      </motion.div>

      {videoPlayer && (
        <motion.div
          className={cx({
            'video-player__container': true,
            'video-player__container-nogradient': !hasGradient,
          })}
          initial={{ opacity: 0 }}
          variants={{
            playing: {
              opacity: 1,
            },
            static: {
              opacity: 0,
            },
          }}
          transition={{ duration: 0.3 }}
        >
          {videoPlayer}
        </motion.div>
      )}
    </motion.div>
  );
};

export default HeroCard;
